import type { ContractParagraph } from '../api';

/*
 * Счётная часть зоны «Документ»: раскладка пузырей на полях (3.56), отбор
 * веток панелью инструментов и поиск абзаца по цитате.
 *
 * Здесь нет ни DOM, ни запросов. Экран меряет высоты пузырей и место якоря,
 * а «куда встать» решается числами — потому это проверяется тестами, а не
 * глазами.
 */

export type Placed<T> = { item: T; top: number; pushed: boolean };

/**
 * Пузыри сверху вниз: каждый встаёт напротив своего выделения, а наехавший на
 * предыдущий сдвигается вниз с зазором и помечается `pushed` — у такого
 * выноска ломается вверх, к своему якорю.
 */
export function placeNotes<T>(
  items: { item: T; anchorTop: number; height: number }[],
  gap = 8,
): Placed<T>[] {
  const out: Placed<T>[] = [];
  let floor = -Infinity;
  for (const it of items) {
    const top = Math.max(it.anchorTop, floor);
    out.push({ item: it.item, top, pushed: top > it.anchorTop });
    floor = top + it.height + gap;
  }
  return out;
}

/** Пробелы в один: цитата приходит со страницы PDF, абзац — из слепка docx. */
const flat = (text: string): string => text.replace(/\s+/g, ' ').trim();

/**
 * Абзац слепка, в котором лежит цитата: номер нужен новому замечанию — по
 * нему сервер подписывает ветку номером пункта и переносит якорь на
 * следующую версию.
 */
export function paragraphOf(paragraphs: ContractParagraph[], quote: string): ContractParagraph | null {
  const needle = flat(quote);
  if (!needle) return null;
  return paragraphs.find((p) => flat(p.text).includes(needle)) ?? null;
}

/**
 * Разбор выбранной версии: пока он не прочитан, пункта у нового замечания
 * нет. Состояние держится явным — молчаливый пустой список не отличить от
 * прочитанного договора без абзацев.
 */
export type SnapshotState =
  | { state: 'loading' }
  | { state: 'ready'; paragraphs: ContractParagraph[] }
  | { state: 'failed' };

export const NO_PARAGRAPH = 'Не удалось определить пункт — обновите страницу';

/**
 * Пункт неизвестен. Угаданный ноль — это заголовок договора: сервер по такому
 * номеру подписал бы ветку чужим пунктом. Отрицательный номер не совпадает ни
 * с одним абзацем, поэтому подписи не будет вовсе, а место ветки сервер
 * найдёт по цитате и проставит номер при переносе на следующую версию.
 */
export const NO_CLAUSE = -1;

export const NO_CLAUSE_HINT = 'Пункт определить не удалось: место замечания сервер найдёт по цитате';

/**
 * Состояние формы нового замечания. Разбор версии — дело сервера, и он
 * необязателен: у гостя его нет вовсе (`null`), а у версии, которую не
 * удалось разобрать, он остаётся `failed` навсегда. Ни то ни другое не повод
 * запирать форму: сервер примет замечание и без номера пункта. Ждать стоит
 * только пока разбор читается — иначе пункт потерялся бы на ровном месте.
 */
export function draftState(snap: SnapshotState | null): { ready: boolean; hint: string } {
  if (!snap || snap.state === 'ready') return { ready: true, hint: '' };
  if (snap.state === 'failed') return { ready: true, hint: NO_CLAUSE_HINT };
  return { ready: false, hint: 'Читаю разбор версии' };
}

/**
 * Пункт нового замечания: он есть, только когда разбор версии прочитан и в
 * нём нашлась цитата. Разбора нет вовсе или он не удался — замечание уходит
 * без номера (`NO_CLAUSE`), как и у гостя.
 */
export function draftParagraph(snap: SnapshotState | null, quote: string): { i: number } | { error: string } {
  if (!snap || snap.state === 'failed') return { i: NO_CLAUSE };
  if (snap.state !== 'ready') return { error: NO_PARAGRAPH };
  const p = paragraphOf(snap.paragraphs, quote);
  return p ? { i: p.i } : { error: NO_PARAGRAPH };
}

/** Подсказка «п. N» у кнопки «Комментировать»: без номера у абзаца её нет. */
export function clauseHint(paragraphs: ContractParagraph[], quote: string): string {
  const p = paragraphOf(paragraphs, quote);
  const label = p ? p.label.replace(/\.$/, '') : '';
  return label ? `п. ${label}` : '';
}
